/**
 * PostHog Feature Flags Hooks
 *
 * Re-exports the official posthog-js/react hooks with Gold Center documentation.
 * These are thin wrappers — use them instead of importing from posthog-js/react directly
 * so we have a single place to document and adjust behavior.
 *
 * ## How Feature Flags Work
 *
 * 1. **Define flags** in the PostHog dashboard (analytics.goldcenter.am):
 *    - Go to Feature Flags → New Feature Flag
 *    - Set a key (e.g., "new-price-card-layout")
 *    - Configure rollout % or user targeting rules
 *
 * 2. **Check flags** in code:
 *    ```tsx
 *    const isEnabled = useFeatureFlag('new-price-card-layout')
 *
 *    return isEnabled ? <NewPriceCard /> : <OldPriceCard />
 *    ```
 *
 * 3. **Multivariate flags** (A/B/C testing):
 *    ```tsx
 *    const variant = useFeatureFlagVariant('checkout-flow')
 *    // variant is 'control' | 'variant-a' | 'variant-b' | undefined
 *    ```
 *
 * 4. **Flag payloads** (pass config data alongside the flag):
 *    ```tsx
 *    const result = useFeatureFlagResult('price-refresh-rate')
 *    // result?.payload could be { interval: 500 } set in PostHog dashboard
 *    ```
 *
 * ## Lifecycle
 * - Flags are fetched from PostHog on SDK init (first pageload)
 * - Cached locally — subsequent checks are instant (no network call)
 * - Auto-refresh when PostHog decides endpoint changes
 * - Hooks re-render when the flag value changes
 *
 * ## Testing locally
 * - Override flags via PostHog toolbar (analytics.goldcenter.am → Toolbar)
 * - Or via browser console: `posthog.featureFlags.override({'flag-key': true})`
 * - Clear overrides: `posthog.featureFlags.override(false)`
 *
 * ## When PostHog is not initialized
 * - `useFeatureFlag` returns `undefined` (falsy — safe in conditionals)
 * - `useFeatureFlagVariant` returns `undefined`
 * - `useFeatureFlagResult` returns `undefined`
 * These hooks are safe to call even when NEXT_PUBLIC_POSTHOG_KEY is missing.
 */

'use client'

// Re-export official hooks from posthog-js/react
// These properly emit $feature_flag_called events and handle all edge cases.
export {
  useFeatureFlagEnabled as useFeatureFlag,
  useFeatureFlagVariantKey as useFeatureFlagVariant,
  useFeatureFlagResult,
} from 'posthog-js/react'
